import { ReactNode, useState } from "react";
import { AnimateWordsOnScroll } from "../../../components/animate/animateWords";
import { ScribbleReveal } from "../../../components/animate/scribbleReveal";
import { Icon } from "../../../components/icon/icon";
import { TabNav } from "../../../components/tabNav";
import { PayoutCurrency } from "../../../redux/features/payment/payment.types";
import { currencyFlags, PRODUCT_PRICING } from "../../../utils/constants";
import { scribblePaths } from "../data/landingContent";

const offers = [
  "Unlimited Events (Free & Paid)",
  "Next-Day Payouts",
  "Customisable Discount codes",
  "Automated Event Reminders",
  "QR Code Check-In",
  "Show Location After Ticket Purchase",
  "Secure Payments with Paystack",
  "Invite Co-organizers",
  "Email Blast To Guests",
];

const comingSoon = ["Recurring Events"];

const currencyOptions: {
  title: string;
  to: PayoutCurrency;
}[] = [
  {
    title: "Nigeria",
    to: "NGN",
  },
  {
    title: "Canada",
    to: "Canadian Dollars",
  },
];

const Card = ({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) => {
  return (
    <div
      className={`landing-premium-card rounded-[24px] border border-white/80 bg-white/60 shadow-[0px_18px_54px_rgba(9,21,37,0.10)] backdrop-blur-xl ${className}`}
    >
      {children}
    </div>
  );
};

export const PricingPage = () => {
  const [currency, setCurrency] = useState<PayoutCurrency>("NGN");

  return (
    <div className="relative overflow-hidden pb-14 pt-16 md:pb-20 md:pt-20">
      <div className="max-width-website relative">
        <div className="mx-auto flex w-full max-w-[680px] flex-col items-center gap-4 text-center">
          <h1 className="flex flex-col items-center font-dela text-[32px] leading-[38px] tracking-[-0.64px] text-ink md:text-[48px] md:leading-[50px] md:tracking-[-0.96px]">
            <span className="relative z-[1] inline-block">
              <ScribbleReveal
                {...scribblePaths.hero}
                className="pointer-events-none absolute left-1/2 top-[58%] -z-10 w-[170px] -translate-x-1/2 -translate-y-1/2 md:w-[260px]"
              />
              <AnimateWordsOnScroll startIndex={0}>
                Simple,
              </AnimateWordsOnScroll>
            </span>
            <AnimateWordsOnScroll startIndex={1}>
              Transparent Pricing
            </AnimateWordsOnScroll>
          </h1>

          <p className="font-onest text-[20px] font-normal leading-[28px] tracking-[-0.4px] text-ink">
            Tikket only makes money when you do
          </p>
        </div>

        <div className="mx-auto mt-8 w-fit md:mt-10">
          <TabNav
            navs={currencyOptions.map((opt) => {
              return {
                ...opt,
                title: (
                  <>
                    {currencyFlags[opt.to]}&nbsp;&nbsp;{opt.title}
                  </>
                ),
                onClick: () => {
                  setCurrency(opt.to);
                },
              };
            })}
            activeTab={currency}
            variant="switcher"
            className="!rounded-full !border-[5px] !border-grey-100 !bg-grey-100 !p-0 !shadow-none"
            tabClassName="!rounded-full !px-6 !py-2 !font-onest !text-[16px] !font-medium !text-[#50597d]"
          />
        </div>

        <div className="pricing-parent-card mx-auto mt-8 flex w-full max-w-[920px] flex-col gap-3 rounded-[28px] border border-white/80 bg-white/45 p-3 backdrop-blur-xl">
          <div>
            <Card className="flex flex-row flex-wrap gap-4 p-4 md:p-6">
              <div className="flex-1">
                <p className="font-onest text-[18px] font-medium tracking-[-0.18px] text-grey-700">
                  Our Fee Per Paid Ticket
                </p>
                <p className="mt-2 whitespace-nowrap font-dela text-[32px] leading-[34px] tracking-[-0.64px] text-ink md:text-[48px] md:leading-[48px] md:tracking-[-0.96px]">
                  {PRODUCT_PRICING[currency].feeText}
                </p>
              </div>

              <div className="flex h-fit min-w-fit items-center gap-2 rounded-[200px] border border-[#C1DBF1] bg-[#EDF3FB] px-3 py-2 text-blue">
                <Icon
                  svg="info"
                  width={20}
                  height={20}
                  className="rotate-180"
                />
                <p className="font-onest text-[18px] font-medium tracking-[-0.18px]">
                  No fees on free tickets
                </p>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Card className="flex flex-col flex-wrap gap-4 p-4 md:px-6 md:py-5">
                <p className="font-onest text-[18px] font-medium tracking-[-0.18px] text-blue">
                  What We Offer
                </p>

                <div className="flex flex-col gap-4">
                  {offers.map((offer, idx) => {
                    return (
                      <div
                        key={`offer-${idx}`}
                        className="flex items-center gap-[6px]"
                      >
                        <Icon
                          svg="check"
                          width={20}
                          height={20}
                          className="text-[#82C99A]"
                        />
                        <p className="font-onest text-[18px] font-medium tracking-[-0.18px] text-grey-900">
                          {offer}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>

            <div>
              <Card className="flex h-full flex-col flex-wrap gap-4 p-4 md:px-6 md:py-5">
                <p className="font-onest text-[18px] font-medium tracking-[-0.18px] text-blue">
                  Coming Soon
                </p>

                <div className="flex flex-col gap-4">
                  {comingSoon.map((offer, idx) => {
                    return (
                      <div
                        key={`offer-${idx}`}
                        className="flex items-center gap-[6px]"
                      >
                        <Icon
                          svg="circle-minus"
                          width={20}
                          height={20}
                          className="text-[#95A9C5]"
                        />
                        <p className="font-onest text-[18px] font-medium tracking-[-0.18px] text-grey-900">
                          {offer}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
